// Map #8 — Crystal Desert (sim half). A night-time sea of wind-rippled dunes under a huge moon: humming teal / violet crystal
// spires, glowing crystal shards, the half-buried bones of giant ancient creatures, glass cacti and dry scrub.
// Three light, telegraphed hazards (owner 2026-11-02: hazards hit the hero AND the foes, always announced first):
//   SANDSTORM      (fog, whole arena)  every ~2:10 from ~1:40: 6 s warning (the wind picks up, a dust front rolls in), then
//                                      20 s of blowing sand: visibility 45 %, everyone ~7 % slower
//   SINKING SAND   (slow, 8 patches)   dormant basins that churn open for 26 s at a time (3 s warning): speed x0.55
//   CRYSTAL PULSE  (burn, 5 spires)    a spire's glow builds for 2.6 s, then a 1.4 s discharge ring around its base:
//                                      5 HP/s on the hero (~7 HP a pulse), 7.5 % max HP/s on foes (bosses a quarter)
// Pure: no three / DOM / unseeded randomness (check:ip enforces it). Everything random comes from the seeded ctx.rng.
import type { HazardDef } from '../../core/hazards';
import type { HazardCtx, MapCoreDef } from '../../core/mapdef';
import type { VariantDef } from '../../core/terrain';

/** Tree archetype on this map: crystal flora (render: src/maps/crystal-desert/models.ts builds one shape per entry, in order). */
export const CRYSTAL_FLORA: VariantDef[] = [
  { name: 'glasscactus', r: 0.55, s: [0.85, 1.15], h: [0.9, 1.15], hp: 1, w: 3 },   // ribbed column cactus, crystal-tipped arms
  { name: 'shardbloom', r: 0.6, s: [0.8, 1.1], h: [0.85, 1.1], hp: 0.9, w: 2 },     // a fan of crystal blades on a stony stem
  { name: 'bleachwood', r: 0.45, s: [0.9, 1.2], h: [0.95, 1.15], hp: 0.8, w: 2 },   // dead desert tree with crystal growths
];
/** Bush archetype: low desert growth (hit radius at s = 1 includes the renderer's bush scale). */
export const DESERT_SCRUB: VariantDef[] = [
  { name: 'saltbrush', r: 0.95, s: [0.8, 1.2], h: [0.85, 1.1], hp: 1, w: 3 },     // dry silver-green scrub mound
  { name: 'spikerosette', r: 0.85, s: [0.8, 1.15], h: [0.9, 1.1], hp: 0.9, w: 2 }, // rosette of glassy spikes
  { name: 'geode', r: 0.7, s: [0.8, 1.1], h: [0.8, 1.05], hp: 1.2, w: 2 },        // cracked geode, crystals inside
];

/** Hazard numbers (tests and the renderer read them). */
export const CD_HAZ = {
  storm: { first: 100, jitter: 20, warn: 6, active: 20, every: 130, power: 0.55 },
  sink: { count: 8, r: [4.2, 6.2] as [number, number], power: 0.55, warn: 3, active: 26, every: 58, first: 35, gap: 17, minR: 22, spacing: 22 },
  pulse: { spires: 5, pad: 3.2, power: 5, warn: 2.6, active: 1.4, every: 26, first: 60, gap: 11, minR: 20 },
} as const;

/** Sink / pulse counts were authored on the 110 m arena: they scale with the arena area (owner 2026-11-02: 160 m) so the
 *  density stays the same. */
export const cdHazardCounts = (arenaR: number): { sinks: number; pulses: number } => {
  const k = Math.max(1, (arenaR / 110) ** 2);
  return { sinks: Math.round(CD_HAZ.sink.count * k), pulses: Math.round(CD_HAZ.pulse.spires * k) };
};

/** Pure hazard generator: one sandstorm timeline, sinking-sand basins in open sand, discharge rings around a few spires. */
export function crystalDesertHazards({ rng, arenaR, props }: HazardCtx): HazardDef[] {
  const out: HazardDef[] = [];
  const S = CD_HAZ.storm, K = CD_HAZ.sink, P = CD_HAZ.pulse, N = cdHazardCounts(arenaR);
  // 1) sandstorm: the whole arena, repeating
  out.push({ kind: 'fog', global: true, x: 0, z: 0, r: 0, start: S.first + rng.range(0, S.jitter), warn: S.warn, active: S.active, every: S.every, power: S.power, tag: 'storm' });
  // 2) sinking sand: open sand only (clear of solid props), spread out, never near the spawn
  const solid = props.filter((p) => p.kind === 'spire' || p.kind === 'boulder');
  const sinks: { x: number; z: number; r: number }[] = [];
  for (let tries = 0; sinks.length < N.sinks && tries < 400 * Math.ceil(N.sinks / K.count); tries++) {
    const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range((K.minR / arenaR) ** 2, ((arenaR - 16) / arenaR) ** 2)) * arenaR, r = rng.range(K.r[0], K.r[1]);
    const x = Math.cos(a) * d, z = Math.sin(a) * d;
    if (solid.some((p) => Math.hypot(p.x - x, p.z - z) < p.r + r + 2.5)) continue;
    if (sinks.some((s) => Math.hypot(s.x - x, s.z - z) < K.spacing + s.r + r)) continue;
    sinks.push({ x, z, r });
  }
  sinks.forEach((s, i) => out.push({ kind: 'slow', x: s.x, z: s.z, r: s.r, start: K.first + i * K.gap + rng.range(0, 10), warn: K.warn, active: K.active, every: K.every + rng.range(0, 8), power: K.power, tag: 'sink' }));
  // 3) crystal discharge around a few spires (their roots keep pulsing even after the spire itself is shattered)
  const spires = props.filter((p) => p.kind === 'spire' && Math.hypot(p.x, p.z) >= P.minR);
  for (let i = spires.length - 1; i > 0; i--) { const j = rng.int(i + 1); [spires[i], spires[j]] = [spires[j], spires[i]]; }
  spires.slice(0, N.pulses).forEach((p, i) => out.push({ kind: 'burn', x: p.x, z: p.z, r: p.r + P.pad, start: P.first + i * P.gap + rng.range(0, 8), warn: P.warn, active: P.active, every: P.every + rng.range(0, 8), power: P.power, tag: 'pulse' }));
  return out;
}

export const MAP: MapCoreDef = {
  id: 'crystal-desert',
  name: 'Crystal Desert',
  tagline: 'Moonlit dunes and humming crystal spires. Sandstorms blot out the view and sinking sand drags at every step.',
  order: 8,
  status: 'ready',
  time: { default: 'night', day: 'noon' },
  unlock: { after: 'floating-isles', seconds: 600 },
  terrain: {
    // dunes: many broad, soft mounds and a strong long swell; very few dips (render paints the crests and wind ripples)
    hills: { mounds: 24, dip: 0.12, rad: [16, 30], dipRad: [10, 16], amp: [1.0, 2.6], dipAmp: [0.5, 1.0], swell: 0.9, max: 3.0, min: -1.1 },
    props: [
      { kind: 'spire', count: 6, rMin: 24, inset: 8, size: [2.4, 3.8], clear: 14, h: [12, 20], hpPer: 4.5 },          // humming crystal spires
      { kind: 'boulder', count: 12, rMin: 13, inset: 3, size: [1.6, 2.6], clear: 5, h: [0.8, 1.3], hpPer: 2 },         // bones of ancient giants
      { kind: 'rock', count: 36, rMin: 8, inset: 2, size: [0.5, 0.9], clear: 1.2, h: [0.8, 1.3] },                    // crystal shards
      { kind: 'tree', count: 18, rMin: 11, inset: 2, size: [0.6, 0.6], clear: 2.2, h: [1, 1], variants: CRYSTAL_FLORA }, // glass cacti, shard blooms
      { kind: 'bush', count: 34, rMin: 9, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: DESERT_SCRUB, layout: 'clumps' },
    ],
  },
  fire: false, // crystal, bone and sand: nothing catches fire (scorch marks still appear)
  nightPressure: 1.2,
  boss: { name: 'DUNEGLASS BEHEMOTH', hpMul: 1.25 },
  hazards: crystalDesertHazards,
};
